import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Save, Share2, FlaskConical, ChevronRight, TrendingUp, AlertCircle, CheckCircle } from 'lucide-react';
import Layout from '../components/Layout';
import { Btn, Alert, Section, PageHeader, Badge } from '../components/UI';
import { usePatient } from '../contexts/PatientContext';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, API_KEY } from '../config';

function isNormal(result, refRange) {
  if (!result) return true;
  const s = result.toString().toLowerCase().trim();
  if (['normal', 'negative', 'trace'].some(k => s.includes(k))) return true;
  if (['moderate', 'small', 'large', 'high', 'low', 'positive', 'marked'].some(k => s.includes(k))) return false;
  const val = parseFloat(result);
  if (!isNaN(val) && refRange) {
    const m = refRange.match(/([\d.]+)\s*[-:]\s*([\d.]+)/);
    if (m) { const lo = parseFloat(m[1]), hi = parseFloat(m[2]); if (!isNaN(lo) && !isNaN(hi)) return val >= lo && val <= hi; }
    return false;
  }
  return true;
}

export default function ResultPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { patient } = usePatient();
  const { username, userEmail } = useAuth();
  const result = location.state?.result;
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [kitType] = useState(result?.Kit_Type || result?.Result?.Kit_Type || '');
  const now = new Date();

  const diseaseList = result?.Disease || result?.Result?.Disease || [];
  const reportList = result?.Report || result?.Result?.Report || [];
  const abnormalCount = reportList.filter(r => !isNormal(r.Result, r['Ref. Range'])).length;

  if (!result) return (
    <Layout><div style={{ textAlign: 'center', padding: '60px 24px' }}>
      <AlertCircle size={48} color="var(--gray-300)" style={{ margin: '0 auto 16px' }} />
      <p style={{ color: 'var(--gray-500)', fontSize: 16 }}>No result data found.</p>
      <Btn style={{ marginTop: 20 }} onClick={() => navigate('/analysis')}>Run New Analysis</Btn>
    </div></Layout>
  );

  const saveResult = async () => {
    if (!userEmail && !username) { setError('User session not found. Please log in again.'); return; }
    setLoading(true); setError('');
    try {
      const payload = {
        email: userEmail, username, timestamp: now.toISOString(),
        disease: diseaseList, report: reportList,
        full_name: patient?.full_name || '', patient_id: patient?.patient_id || '',
        Kit_Type: kitType || '',
      };
      const res = await fetch(`${API_URL}/api/results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cytometrix-API-KEY': API_KEY },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        setSaved(true);
        if (patient?.patient_id) {
          localStorage.setItem(`result-${patient.patient_id}-${now.getTime()}`, JSON.stringify({ ...payload, id: data.record?.id }));
        }
      } else { setError(data.message || 'Failed to save result.'); }
    } catch { setError('An error occurred while saving.'); }
    finally { setLoading(false); }
  };

  return (
    <Layout>
      <style>{`
        @media (max-width: 600px) {
          .result-summary-cards { grid-template-columns: repeat(3, 1fr) !important; }
          .result-summary-cards .card-label { font-size: 10px !important; }
          .result-info-grid { grid-template-columns: 1fr !important; }
          .result-actions { flex-wrap: wrap; }
          .result-actions > * { flex: 1 1 auto; min-width: 100px; }
          .result-bottom-actions { flex-direction: column !important; }
          .result-bottom-actions > * { width: 100%; }
          .result-table th:nth-child(3),
          .result-table td:nth-child(3) { display: none; }
        }
      `}</style>

      <PageHeader
        title="Analysis Result"
        subtitle={`${now.toLocaleDateString()} at ${now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
        actions={
          <div className="result-actions" style={{ display: 'flex', gap: 8 }}>
            <Btn variant="outline" icon={Share2} onClick={() => navigate('/share', { state: { result, email: userEmail, patientName: patient?.full_name, patientId: patient?.patient_id } })}>Share</Btn>
            {saved
              ? <Btn variant="teal" icon={CheckCircle} disabled>Saved</Btn>
              : <Btn icon={Save} loading={loading} onClick={saveResult}>Save</Btn>}
          </div>
        }
      />

      {error && <div style={{ marginBottom: 16 }}><Alert type="error">{error}</Alert></div>}
      {saved && <div style={{ marginBottom: 16 }}><Alert type="success">Result saved for {patient?.full_name || 'patient'}.</Alert></div>}

      {/* Summary cards */}
      <div className="result-summary-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Parameters', value: reportList.length, icon: FlaskConical, color: 'var(--blue)' },
          { label: 'Abnormal', value: abnormalCount, icon: AlertCircle, color: abnormalCount > 0 ? 'var(--red)' : 'var(--green)' },
          { label: 'Findings', value: diseaseList.length, icon: TrendingUp, color: 'var(--amber)' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} style={{ background: '#fff', borderRadius: 14, padding: '14px 14px', border: '1px solid var(--gray-200)', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
              <Icon size={18} color={color} />
            </div>
            <p style={{ fontSize: 24, fontWeight: 700, color: 'var(--navy)', lineHeight: 1, fontFamily: "'DM Serif Display', serif" }}>{value}</p>
            <p className="card-label" style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 3 }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Patient & Exam Info */}
      <div className="result-info-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <Section title="Patient Information">
          {[['Name', patient?.full_name || 'N/A'], ['Patient ID', patient?.patient_id || 'N/A'], ['Age', patient?.age ? `${patient.age} yrs` : 'N/A']].map(([l, v]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--gray-100)' }}>
              <span style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 600 }}>{l}</span>
              <span style={{ fontSize: 13, color: 'var(--navy)', fontWeight: 600 }}>{v}</span>
            </div>
          ))}
        </Section>
        <Section title="Examination">
          {[['Date', now.toLocaleDateString()], ['Time', now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })], ['Kit', kitType || 'N/A']].map(([l, v]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--gray-100)' }}>
              <span style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 600 }}>{l}</span>
              <span style={{ fontSize: 13, color: 'var(--navy)', fontWeight: 600 }}>{v}</span>
            </div>
          ))}
        </Section>
      </div>

      {/* Diagnostic Insights */}
      {diseaseList.length > 0 && (
        <Section title="🔬 Diagnostic Insights">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {diseaseList.map((d, i) => (
              <div key={i} style={{ background: 'var(--blue-pale)', borderRadius: 12, padding: '14px 16px', borderLeft: '4px solid var(--blue)' }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--blue)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Possible Condition</p>
                <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)', marginBottom: 6 }}>{d['Possible Disease']}</p>
                <p style={{ fontSize: 13, color: 'var(--gray-700)', lineHeight: 1.6 }}>{d['Reason']}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Analysis Table — scrollable on mobile */}
      {reportList.length > 0 && (
        <Section title="📊 Analysis Summary">
          <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid var(--gray-200)', WebkitOverflowScrolling: 'touch' }}>
            <table className="result-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 340 }}>
              <thead>
                <tr style={{ background: 'var(--navy)' }}>
                  {['Parameter', 'Result', 'Ref. Range', 'Status'].map(h => (
                    <th key={h} style={{ padding: '11px 14px', textAlign: 'left', color: '#fff', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportList.map((r, i) => {
                  const normal = isNormal(r.Result, r['Ref. Range']);
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--navy)', whiteSpace: 'nowrap' }}>{r['Analysis Type']}</td>
                      <td style={{ padding: '10px 14px', color: normal ? 'var(--gray-700)' : 'var(--red)', fontWeight: normal ? 400 : 700, fontFamily: "'JetBrains Mono', monospace", fontSize: 12, whiteSpace: 'nowrap' }}>{r.Result}</td>
                      <td style={{ padding: '10px 14px', color: 'var(--gray-500)', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, whiteSpace: 'nowrap' }}>{r['Ref. Range'] || '—'}</td>
                      <td style={{ padding: '10px 14px' }}><Badge color={normal ? 'green' : 'red'}>{normal ? 'Normal' : 'Abnormal'}</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Bottom actions */}
      <div className="result-bottom-actions" style={{ display: 'flex', gap: 12, justifyContent: 'space-between', marginTop: 8 }}>
        <Btn variant="secondary" onClick={() => navigate('/analysis')}><FlaskConical size={15} style={{ marginRight: 6 }} />New Analysis</Btn>
        <Btn variant="ghost" onClick={() => navigate('/')}>View History <ChevronRight size={15} style={{ marginLeft: 4 }} /></Btn>
      </div>

      {patient?.patient_id && (
        <div style={{ marginTop: 14, background: 'var(--blue-pale)', borderRadius: 10, padding: '10px 14px', borderLeft: '3px solid var(--blue)' }}>
          <p style={{ fontSize: 12, color: 'var(--blue)', fontWeight: 500 }}>💡 Saving adds this visit for {patient.full_name} ({patient.patient_id})</p>
        </div>
      )}
    </Layout>
  );
}
